/**
 * The slots a rep types on the availability question card: each is when one call starts, up to
 * five, added and removed here. The end is never typed: it is the start plus the call length, said
 * back under the start (Dawid, ledger `7decf7e1`, "Start + call length";
 * docs/2026-10-09-held-card-quote-and-slot-length.md). The save route derives the same end from the
 * same length (`src/call-length.ts`), so a slot of another length can be neither entered nor saved.
 *
 * Rows are keyed and edited by `id`, never by position, so removing one leaves the others' typed
 * values, open pickers and focus where they were. A card with no row is a valid card: a rep who
 * answers with a booking page and no slot may remove the empty row, and "Add a slot" brings one back.
 */
import { useId, useState } from "react";
import { contextValueSchemas } from "../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";
import { resolveWallClock, zoneClock } from "../src/app-dates.ts";
import type { CallLength } from "../src/call-length-view.ts";
import { showDateTime, showTime, spanInWords, splitDateTime } from "../src/date-entry.ts";
import { DateTimeField, useHeldWhilePressed, zoneCity } from "./date-time-fields.tsx";
import { TimeZoneField } from "./time-zone-field.tsx";

export const MAX_AVAILABILITY_SLOTS = 5;
/** The range a call length has anywhere in the app (`meeting_duration_minutes`). */
export const CALL_MINUTES = { min: 1, max: 480 } as const;

export interface AvailabilitySlot {
  readonly id: string;
  readonly startLocal: string;
}

export function emptySlot(): AvailabilitySlot {
  return { id: crypto.randomUUID(), startLocal: "" };
}

/** A slot counts once it has a start; an empty row is no slot. */
export function slotsToSend(slots: readonly AvailabilitySlot[]): Array<{ startLocal: string }> {
  return slots.filter((slot) => slot.startLocal).map(({ startLocal }) => ({ startLocal }));
}

/**
 * The length the card works to, as the server named it (`src/call-length.ts`). `undefined` while it
 * is still being read; a read that failed asks the rep, and the save route still holds the slots
 * to the server's own length.
 */
export type CardLength = CallLength | undefined;

/** Whether slots can be offered yet: not while the length is being read or its card is unanswered. */
const slotsOpen = (length: CardLength): length is Exclude<CallLength, { kind: "asked" }> =>
  length !== undefined && length.kind !== "asked";

/**
 * Whether the slots can be sent: always when none has a start, and otherwise only with a length to
 * give them. Where no slot can be offered yet, none is sent.
 */
export function slotsComplete(
  slots: readonly AvailabilitySlot[],
  length: CardLength,
  chosen: string,
): boolean {
  if (!slotsOpen(length)) return true;
  return slotsToSend(slots).length === 0 || slotMinutes(length, chosen) !== null;
}

/**
 * The slots part of the posted answer, with the length the card showed them at. A known length is
 * sent back too, so a length that changed after the card loaded is refused rather than saved with
 * an end the rep never saw; a waiting length card sends no slot.
 */
export function slotsBody(
  slots: readonly AvailabilitySlot[],
  length: CardLength,
  chosen: string,
): { slots: Array<{ startLocal: string }>; callMinutes?: number } {
  if (!slotsOpen(length)) return { slots: [] };
  const sent = slotsToSend(slots);
  const minutes = slotMinutes(length, chosen);
  return minutes !== null && sent.length > 0
    ? { slots: sent, callMinutes: minutes }
    : { slots: sent };
}

/** The length typed where the card asks for one, as a whole number of minutes, or null. */
export function chosenMinutes(text: string): number | null {
  // Read as the number field reads it (`1e2` is 100), so the field and the card never disagree.
  const minutes = text.trim() === "" ? Number.NaN : Number(text);
  return Number.isInteger(minutes) && minutes >= CALL_MINUTES.min && minutes <= CALL_MINUTES.max
    ? minutes
    : null;
}

/** The length each slot is: the known one, the one typed where the card asks, or none yet. */
export function slotMinutes(length: CallLength, chosen: string): number | null {
  if (length.kind === "known") return length.minutes;
  return length.kind === "choose" ? chosenMinutes(chosen) : null;
}

/**
 * When a slot ends, as a clock reading on the slot's own clock (`2026-10-12T14:30`).
 *
 * The length is elapsed time, as the save route and the engine count it, so the night the clocks
 * go back a 60-minute slot from 01:45 ends at 02:45 of the repeated hour, not at 03:45. With no zone
 * chosen yet the reading is taken as written.
 */
export function slotEnd(startLocal: string, zone: string, minutes: number): string | null {
  const resolved = zone === "" ? null : resolveWallClock(startLocal, zone);
  if (resolved !== null) {
    if (resolved.kind !== "once") return null;
    const end = zoneClock(zone, resolved.at + minutes * 60_000);
    return end ? `${end.date}T${end.time}` : null;
  }
  const naive = Date.parse(`${startLocal}:00Z`);
  if (Number.isNaN(naive)) return null;
  return new Date(naive + minutes * 60_000).toISOString().slice(0, 16);
}

/** "ends 14:30", or with its day when the call runs past midnight: "ends Tue 13 Oct 2026, 00:15". */
function endWords(start: string, end: string): string {
  const same = splitDateTime(start)?.date === splitDateTime(end)?.date;
  return `ends ${same ? showTime(splitDateTime(end)?.time ?? "") : showDateTime(end)}`;
}

/** What the card says of the length above its slots. */
function lengthLine(length: Extract<CallLength, { kind: "known" }>): string {
  const whose =
    length.source === "conversation"
      ? "the call length given for this conversation"
      : "the call length saved for this campaign";
  return `Each slot is one ${length.minutes}-minute call, ${whose}.`;
}

export function AvailabilitySlots({
  slots,
  onChange,
  zone,
  length,
  chosen,
  onChosen,
  idPrefix,
  now,
}: {
  slots: readonly AvailabilitySlot[];
  onChange: (update: (current: readonly AvailabilitySlot[]) => readonly AvailabilitySlot[]) => void;
  zone: string;
  /** Which length a slot is held to, from the server (`src/call-length.ts`). */
  length: CardLength;
  /** The length typed where the card asks for one; unused otherwise. */
  chosen: string;
  onChosen: (text: string) => void;
  idPrefix: string;
  now?: number | undefined;
}) {
  // Still being read: the slots wait a moment rather than show a length that may be wrong.
  if (length === undefined) return null;
  if (length.kind === "asked")
    return (
      <div className="lcr-slot-group">
        <p className="lcr-answer-note lcr-slot-length">
          Slots open once the call length is answered, because each slot is one call. A booking page
          works now.
        </p>
      </div>
    );
  const minutes = slotMinutes(length, chosen);
  const started = slots.some((slot) => slot.startLocal);
  const edit = (id: string, startLocal: string) =>
    onChange((current) => current.map((item) => (item.id === id ? { ...item, startLocal } : item)));
  // The slots are their own group under a rule, so the length line reads as theirs and not as the
  // booking page's hint above it.
  return (
    <div className="lcr-slot-group">
      {length.kind === "known" ? (
        <p className="lcr-answer-note lcr-slot-length">{lengthLine(length)}</p>
      ) : (
        <div className="lcr-entry-slot lcr-slot-minutes">
          <label htmlFor={`${idPrefix}-call-minutes`}>How long is each call?</label>
          <span className="lcr-slot-minutes-box">
            <input
              id={`${idPrefix}-call-minutes`}
              className="lcr-slot-minutes-input"
              type="number"
              inputMode="numeric"
              min={CALL_MINUTES.min}
              max={CALL_MINUTES.max}
              step={1}
              // Needed once a slot has a start: without it there is no end to give that slot.
              required={started}
              value={chosen}
              aria-describedby={`${idPrefix}-call-minutes-note`}
              onChange={(event) => onChosen(event.target.value)}
            />
            <span aria-hidden="true">minutes</span>
          </span>
          <p className="lcr-answer-note" id={`${idPrefix}-call-minutes-note`}>
            {minutes === null
              ? "No call length is saved for this campaign. Every slot below is one call this long."
              : `Every slot below is one call, ${spanInWords(minutes)}.`}
          </p>
        </div>
      )}
      {slots.map((slot, index) => {
        const end =
          minutes === null || slot.startLocal === ""
            ? null
            : slotEnd(slot.startLocal, zone, minutes);
        return (
          <div className="lcr-slot-row" key={slot.id}>
            <div className="lcr-entry-slot lcr-slot-start">
              <label htmlFor={`${slot.id}-start`}>Slot {index + 1} starts</label>
              <DateTimeField
                id={`${slot.id}-start`}
                zone={zone}
                now={now}
                notPast
                remark={end === null ? undefined : endWords(slot.startLocal, end)}
                value={slot.startLocal}
                onChange={(startLocal) => edit(slot.id, startLocal)}
              />
            </div>
            <button
              type="button"
              onClick={() => onChange((current) => current.filter((item) => item.id !== slot.id))}
            >
              Remove slot {index + 1}
            </button>
          </div>
        );
      })}
      {slots.length < MAX_AVAILABILITY_SLOTS && (
        <button type="button" onClick={() => onChange((current) => [...current, emptySlot()])}>
          {slots.length === 0 ? "Add a slot" : "Add another slot"}
        </button>
      )}
    </div>
  );
}

/** Everything the availability card holds before it is sent. */
export interface AvailabilityDraft {
  readonly zone: string;
  readonly url: string;
  readonly slots: readonly AvailabilitySlot[];
  /** The length typed where the card asks for one. */
  readonly chosen: string;
}

export function emptyAvailability(): AvailabilityDraft {
  return { zone: "", url: "", slots: [emptySlot()], chosen: "" };
}

/** Whether the card may be saved: a real zone, and a booking page or at least one slot to offer. */
export function availabilityComplete(draft: AvailabilityDraft, length: CardLength): boolean {
  return (
    contextValueSchemas.rep_timezone.safeParse(draft.zone.trim()).success &&
    slotsComplete(draft.slots, length, draft.chosen) &&
    (draft.url.trim().length > 0 || slotsBody(draft.slots, length, draft.chosen).slots.length > 0)
  );
}

/** The typed answer the card posts (`typed` on the answers route). */
export function availabilityBody(draft: AvailabilityDraft, length: CardLength) {
  return {
    key: "availability" as const,
    timezone: draft.zone.trim(),
    ...(draft.url.trim() ? { bookingUrl: draft.url.trim() } : {}),
    ...slotsBody(draft.slots, length, draft.chosen),
  };
}

/**
 * Under which zone's clock each typed slot was typed, by the slot's id. A slot keeps its clock time
 * when "Time zone for these times" is changed, which makes it another moment (Dawid, terminal
 * picker 2026-10-13, ledger `fd11b5a3`: "Keep the clock, say so"), and this is how the card knows
 * which slots that happened to. It lives in the card and nowhere else: it is gone once the card is
 * saved or reloaded, and nothing of it is posted.
 */
export type TypedSlots = ReadonlyMap<string, { readonly start: string; readonly zone: string }>;

/**
 * The record after the card changed. A time typed or retyped belongs to the zone chosen then, so a
 * slot typed after a zone change, or retyped after one, is on the new clock and did not move. A
 * time typed before any zone was chosen takes the first zone chosen. An emptied zone box changes
 * nothing: Warsaw, emptied, New York is still a move, and Warsaw again is none. Returns `before`
 * itself when nothing changed.
 */
export function trackTyped(
  before: TypedSlots,
  slots: readonly AvailabilitySlot[],
  zone: string,
): TypedSlots {
  const after = new Map<string, { start: string; zone: string }>();
  let same = true;
  for (const slot of slots) {
    if (slot.startLocal === "") continue;
    const was = before.get(slot.id);
    if (was && was.start === slot.startLocal && (was.zone !== "" || zone === "")) {
      after.set(slot.id, was);
      continue;
    }
    after.set(slot.id, { start: slot.startLocal, zone });
    same = false;
  }
  return same && after.size === before.size ? before : after;
}

/** Which slots, by their number on the card, hold a time typed on another zone's clock. */
export function movedSlots(
  typed: TypedSlots,
  slots: readonly AvailabilitySlot[],
  zone: string,
): readonly number[] {
  if (zone === "") return [];
  return slots.flatMap((slot, index) => {
    // An empty row has no record. A typed one has its zone, which is never "" once one is chosen.
    const from = typed.get(slot.id)?.zone;
    return from !== undefined && from !== zone ? [index + 1] : [];
  });
}

/**
 * The clock a zone is called by. A place gives the words the slot's own note uses, "New York
 * time". A zone that is no place is called by the id the rep typed and the zone box shows:
 * `Etc/GMT+5` is five hours behind UTC, and "GMT+5 time" would say the opposite.
 */
const clockName = (zone: string): string =>
  zone === "UTC" || zone.startsWith("Etc/") ? zone : `${zoneCity(zone)} time`;

const inWords = (numbers: readonly number[]): string =>
  numbers.length < 2
    ? numbers.join("")
    : `${numbers.slice(0, -1).join(", ")} and ${numbers.at(-1)}`;

/**
 * What the card says under the zone while typed slots sit on another zone's clock than the one
 * they were typed on, or `null`. Where that is every typed slot they are "your slots"; where some
 * were typed since, the moved ones are named by the number in their label.
 */
export function zoneMovedNote(
  zone: string,
  moved: readonly number[],
  typed: number,
): string | null {
  if (moved.length === 0) return null;
  const one = moved.length === 1;
  const which =
    moved.length === typed
      ? one
        ? "Your slot"
        : "Your slots"
      : `${one ? "Slot" : "Slots"} ${inWords(moved)}`;
  return one
    ? `${which} keeps the time you typed and is now in ${clockName(zone)}.`
    : `${which} keep the times you typed and are now in ${clockName(zone)}.`;
}

/** The availability card's fields: whose clock, the booking page, and the slots. */
export function AvailabilityFields({
  idPrefix,
  prompt,
  draft,
  onChange,
  length,
}: {
  idPrefix: string;
  prompt: string | undefined;
  draft: AvailabilityDraft;
  onChange: (update: (current: AvailabilityDraft) => AvailabilityDraft) => void;
  length: CardLength;
}) {
  const set = (change: Partial<AvailabilityDraft>) =>
    onChange((current) => ({ ...current, ...change }));
  const [typed, setTyped] = useState<TypedSlots>(() =>
    trackTyped(new Map(), draft.slots, draft.zone),
  );
  const tracked = trackTyped(typed, draft.slots, draft.zone);
  if (tracked !== typed) setTyped(tracked);
  const movedNoteId = useId();
  /*
   * Held for the length of a press, as the lines beside it are: the usual way to leave the zone box
   * is to press Save, the zone is taken as the box loses focus, and a line appearing or going then
   * would move the button out from under the pointer.
   */
  const movedNote = useHeldWhilePressed(
    zoneMovedNote(
      draft.zone,
      movedSlots(tracked, draft.slots, draft.zone),
      slotsToSend(draft.slots).length,
    ),
  );
  return (
    <div className="lcr-answer-typed">
      <p>{prompt}</p>
      <div className="lcr-entry-slot">
        <label htmlFor={`${idPrefix}-zone`}>Time zone for these times</label>
        <TimeZoneField
          id={`${idPrefix}-zone`}
          required
          value={draft.zone}
          onChange={(zone) => set({ zone })}
          {...(movedNote ? { describedBy: movedNoteId } : {})}
        />
        {/* On the page before it has anything to say: a status that arrives together with its
            words is announced unreliably. Empty, it takes no room (styles.css). */}
        <div className="lcr-zone-moved" id={movedNoteId} role="status">
          {movedNote && <p className="lcr-entry-note">{movedNote}</p>}
        </div>
      </div>
      <label>
        Booking page, if you use one
        <input
          type="url"
          value={draft.url}
          placeholder="https://"
          onChange={(event) => set({ url: event.target.value })}
        />
      </label>
      <AvailabilitySlots
        slots={draft.slots}
        onChange={(update) => onChange((current) => ({ ...current, slots: update(current.slots) }))}
        zone={draft.zone}
        length={length}
        chosen={draft.chosen}
        onChosen={(chosen) => set({ chosen })}
        idPrefix={idPrefix}
      />
    </div>
  );
}
